import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SDKProvider, useLaunchParams } from '@telegram-apps/sdk-react'
import { TelegramSDKBridge } from './hooks/TelegramProvider'
import { App } from './App'
import { RootErrorBoundary } from './components/RootErrorBoundary'
import './index.css'

/**
 * #321 (8-й заход): «по дням одна инфа, в аналитике другая», «иногда есть,
 * иногда нет», «перезаход помогает».
 *
 * Данные протухают через минуту, но перезапрашиваются только при новом
 * монтировании запроса. Возврат фокуса рефетч не вызывал — он был выключен.
 * В Telegram Desktop мини-апп живёт неделями одним открытым окном: куратор
 * возвращается к давно открытой карточке, компонент смонтирован, и он листает
 * дни по данным, загруженным часы назад. Аналитику он открывает позже — она
 * монтируется заново и приходит свежей. Отсюда и «в дневнике одно, в аналитике
 * другое», и то, что помогает именно полное закрытие окна.
 *
 * Молчание маяка day-metrics-mismatch с этим согласуется: дни совпадают,
 * устаревают сами цифры, а не соответствие дня записи выбранному.
 *
 * Возврат в окно и восстановление сети снова обновляют данные. Лишней нагрузки
 * это не даёт: staleTime прежний, свежие данные не перезапрашиваются.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
})

function TelegramApp() {
  // ssr=true variant returns `undefined` instead of throwing when launch
  // params are not retrievable (e.g. after a Telegram "Обновить страницу"
  // that drops the URL hash → unhandled throw would blank the whole tree).
  const launchParams = useLaunchParams(true)
  const debug = launchParams?.startParam === 'debug'

  return (
    <SDKProvider acceptCustomStyles debug={debug}>
      <TelegramSDKBridge>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </TelegramSDKBridge>
    </SDKProvider>
  )
}

// For development outside Telegram — no SDKProvider, useTelegram returns defaults via context
function MockApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  )
}

// Detect a REAL Telegram client by `platform`, NOT by `initData`. On reopen
// after WebApp.close() Telegram can transiently return an empty initData;
// keying off it would misclassify a real session as "browser" → MockApp →
// dev auto-login as the test persona (NBL_Dev curator). `platform` is set by
// telegram-web-app.js (loaded before this module) and is only 'unknown' outside
// Telegram. Inside TelegramApp the SDK recovers initData from the launch params.
const tgPlatform = window.Telegram?.WebApp?.platform
const isTelegram = !!tgPlatform && tgPlatform !== 'unknown'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      {isTelegram ? <TelegramApp /> : <MockApp />}
    </RootErrorBoundary>
  </React.StrictMode>
)

// Declare Telegram WebApp on window
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string
        initDataUnsafe?: Record<string, unknown>
        platform?: string
        ready?: () => void
        expand?: () => void
        close?: () => void
      }
    }
  }
}
